import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  arrayUnion,
  arrayRemove,
  getDocs,
  limit,
  increment,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from './useAuth';

export const useSharing = () => {
  const { user } = useAuth();
  const [sharedWithMe, setSharedWithMe] = useState([]);      // accepted shares where I am recipient
  const [pendingShares, setPendingShares] = useState([]);    // pending share invites I received
  const [mySharedItems, setMySharedItems] = useState([]);    // shares I sent (any status)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Real-time listeners
  useEffect(() => {
    if (!user?.uid) return;

    // Accepted shares sent TO me
    const acceptedQ = query(
      collection(db, 'shares'),
      where('sharedWithId', '==', user.uid),
      where('status', '==', 'accepted'),
      orderBy('createdAt', 'desc')
    );
    const unsubAccepted = onSnapshot(acceptedQ, (snap) => {
      setSharedWithMe(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, (err) => { setError(err.message); setLoading(false); });

    // Pending shares sent TO me
    const pendingQ = query(
      collection(db, 'shares'),
      where('sharedWithId', '==', user.uid),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );
    const unsubPending = onSnapshot(pendingQ, (snap) => {
      setPendingShares(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    // Shares I sent (owner view)
    const myQ = query(
      collection(db, 'shares'),
      where('ownerId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );
    const unsubMy = onSnapshot(myQ, (snap) => {
      setMySharedItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    return () => { unsubAccepted(); unsubPending(); unsubMy(); };
  }, [user?.uid]);

  // Search users by email (for share dialog)
  const searchUsers = async (emailQuery) => {
    if (!emailQuery?.trim() || !user?.uid) return [];
    try {
      const q = query(
        collection(db, 'users'),
        where('email', '==', emailQuery.trim().toLowerCase()),
        limit(5)
      );
      const snap = await getDocs(q);
      return snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(u => u.id !== user.uid); // exclude self
    } catch (err) {
      setError(err.message);
      return [];
    }
  };

  // Max active shares for free users
  const FREE_SHARE_LIMIT = 5;

  const tier = user?.subscription?.tier || 'free';

  // Can this user send another share?
  const canShare = () => {
    if (tier === 'lifetime') return true;
    if (tier === 'pro') return (user?.shareCredits ?? 0) > 0;
    // free: cap on total active shares
    const activeShares = mySharedItems.filter(s => s.status !== 'declined').length;
    return activeShares < FREE_SHARE_LIMIT;
  };

  // How many shares remain (display value)
  const remainingShares = () => {
    if (tier === 'lifetime') return Infinity;
    if (tier === 'pro') return user?.shareCredits ?? 0;
    const activeShares = mySharedItems.filter(s => s.status !== 'declined').length;
    return Math.max(0, FREE_SHARE_LIMIT - activeShares);
  };

  // Send a share invite
  const sendShareInvite = async ({ type, resourceId, resourceTitle, toUserId, toUserEmail, toUserName }) => {
    if (!user?.uid) throw new Error('Must be logged in');

    // Enforce sharing limits
    if (!canShare()) {
      if (tier === 'pro') {
        throw new Error('You have no share credits left this month. Credits refresh on the 1st of next month.');
      }
      throw new Error(`Free accounts are limited to ${FREE_SHARE_LIMIT} active shares. Upgrade to Pro or Lifetime for more.`);
    }

    // Check not already shared
    const existing = mySharedItems.find(
      s => s.resourceId === resourceId && s.sharedWithId === toUserId && s.status !== 'declined'
    );
    if (existing) throw new Error('Already shared with this user');

    try {
      // Create the share doc
      const shareRef = await addDoc(collection(db, 'shares'), {
        type,              // 'sheet' | 'folder'
        resourceId,
        resourceTitle,
        ownerId: user.uid,
        ownerName: user.displayName || user.email,
        ownerEmail: user.email,
        sharedWithId: toUserId,
        sharedWithEmail: toUserEmail,
        permission: 'view',
        status: 'pending',
        createdAt: new Date().toISOString(),
      });

      // Create a notification for the recipient
      await addDoc(collection(db, 'notifications'), {
        userId: toUserId,
        type: 'share_invite',
        payload: {
          shareId: shareRef.id,
          resourceType: type,
          resourceTitle,
          fromUserId: user.uid,
          fromUserName: user.displayName || user.email,
        },
        read: false,
        createdAt: new Date().toISOString(),
      });

      // Deduct a share credit for Pro users
      if (tier === 'pro') {
        await updateDoc(doc(db, 'users', user.uid), {
          shareCredits: increment(-1),
        });
      }

      return shareRef.id;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Accept or decline a share invite
  const respondToShare = async (shareId, accept) => {
    if (!user?.uid) return;
    try {
      const shareDocRef = doc(db, 'shares', shareId);
      const share = pendingShares.find(s => s.id === shareId) ||
                    sharedWithMe.find(s => s.id === shareId);

      await updateDoc(shareDocRef, {
        status: accept ? 'accepted' : 'declined',
        respondedAt: new Date().toISOString(),
      });

      if (accept && share) {
        // Add recipient to the resource's sharedWith array
        const collName = share.type === 'folder' ? 'folders' : 'sheets';
        await updateDoc(doc(db, collName, share.resourceId), {
          sharedWith: arrayUnion(user.uid),
        });

        // Notify the owner that the share was accepted
        await addDoc(collection(db, 'notifications'), {
          userId: share.ownerId,
          type: 'share_accepted',
          payload: {
            shareId,
            resourceType: share.type,
            resourceTitle: share.resourceTitle,
            byUserId: user.uid,
            byUserName: user.displayName || user.email,
          },
          read: false,
          createdAt: new Date().toISOString(),
        });
      }
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Revoke a share (owner removes access)
  const revokeShare = async (shareId) => {
    if (!user?.uid) return;
    try {
      const share = mySharedItems.find(s => s.id === shareId);
      if (!share) return;

      // Remove from resource's sharedWith array
      const collName = share.type === 'folder' ? 'folders' : 'sheets';
      await updateDoc(doc(db, collName, share.resourceId), {
        sharedWith: arrayRemove(share.sharedWithId),
      });

      // Delete the share doc
      await deleteDoc(doc(db, 'shares', shareId));
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // Get all shares for a specific resource (for the share modal)
  const getSharesForResource = async (resourceId) => {
    try {
      const q = query(
        collection(db, 'shares'),
        where('ownerId', '==', user.uid),
        where('resourceId', '==', resourceId)
      );
      const snap = await getDocs(q);
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (err) {
      setError(err.message);
      return [];
    }
  };

  return {
    sharedWithMe,
    pendingShares,
    mySharedItems,
    loading,
    error,
    searchUsers,
    sendShareInvite,
    respondToShare,
    revokeShare,
    getSharesForResource,
    canShare,
    remainingShares,
    FREE_SHARE_LIMIT,
  };
};

export default useSharing;
